// src/encounter/intelligence/brief.ts
//
// The code-built pre-visit brief, `scrips.intelligence.brief/1`, as the engine sends it from POST
// /api/intelligence/cards/brief (scrips-clinician-demo src/engine/briefCard.ts). Written by code from the record, never by
// a model, labelled "From the record" (encounter owner, 1 Nov 2026, after the agent value test). A2: the payload is
// checked on arrival; anything malformed is null and the card shows the technical line, never the body.

export const BRIEF_SCHEMA = 'scrips.intelligence.brief/1';
export type BriefSectionId = 'critical' | 'new' | 'pending' | 'retests' | 'not-on-file' | 'separate-labs' | 'medicines';
const SECTION_IDS: readonly BriefSectionId[] = ['critical', 'new', 'pending', 'retests', 'not-on-file', 'separate-labs', 'medicines'];

export interface BriefLine { text: string; cites: string[] }
export interface BriefSection { id: BriefSectionId; title: string; lines: BriefLine[]; emptyLine?: string }
export interface BriefMarker { analyte: string; date: string; label: string; recordId: string }
export interface BriefV1 {
  schema: typeof BRIEF_SCHEMA;
  origin: { kind: 'from-record'; label: string };
  readLine: string;
  limits: string[];
  sections: BriefSection[];
  markers: BriefMarker[];
  asOf: string;
  auditId?: string;
}

const MAX = { text: 400, title: 120, sections: 7, lines: 60, cites: 20, id: 160, markers: 40, limits: 6 };
type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, max = MAX.text): v is string => typeof v === 'string' && v.length > 0 && v.length <= max;
const okLine = (v: unknown) => isObj(v) && str(v.text) && Array.isArray(v.cites) && v.cites.length <= MAX.cites && v.cites.every((c) => str(c, MAX.id));
const okSection = (v: unknown) =>
  isObj(v) && SECTION_IDS.includes(v.id as BriefSectionId) && str(v.title, MAX.title) && Array.isArray(v.lines) && v.lines.length <= MAX.lines && v.lines.every(okLine) && (v.emptyLine === undefined || str(v.emptyLine));
const okMarker = (v: unknown) => isObj(v) && str(v.analyte, MAX.id) && /^\d{4}-\d{2}-\d{2}$/.test(String(v.date)) && str(v.label) && str(v.recordId, MAX.id);

/** The checked payload, or null. Only a code-built card ("from-record") is ever accepted. */
export function parseBrief(raw: unknown): BriefV1 | null {
  if (!isObj(raw) || raw.schema !== BRIEF_SCHEMA) return null;
  if (!isObj(raw.origin) || raw.origin.kind !== 'from-record' || !str(raw.origin.label, MAX.title)) return null;
  if (!str(raw.readLine) || !str(raw.asOf, 40)) return null;
  if (!Array.isArray(raw.limits) || raw.limits.length > MAX.limits || !raw.limits.every((l) => str(l))) return null;
  if (!Array.isArray(raw.sections) || raw.sections.length > MAX.sections || !raw.sections.every(okSection)) return null;
  if (!Array.isArray(raw.markers) || raw.markers.length > MAX.markers || !raw.markers.every(okMarker)) return null;
  if (raw.auditId !== undefined && !str(raw.auditId, MAX.id)) return null;
  return raw as unknown as BriefV1;
}
